import moment, { Moment } from "moment";
import {
  GADimensions,
  GAEvents,
  PinnedVisualType,
  User,
  pnlFormat,
} from "../../interfaces/interfaces";
import parse from "html-react-parser";
import * as htmlToImage from "html-to-image";
import meritoIcon from "../../resources/images/MeritoIcon.png";
import CryptoJS from "crypto-js";
import ReactGA from "react-ga4";
import { RootState } from "../../redux/store";
import { RestS } from "../../rest/rest-service";
import {
  setCustomDataPeiodJobId,
  setCustomDataPeiodStatus,
  setData,
  setJobId,
  setStatus,
} from "../../redux/agencyDashboard/agencyDashboardSlice";
import { AgJobsStatus } from "../../pages/Dashboard/Components/AgencyDashboardTabs/AgencyDashboardTabs";

export function getTime(time) {
  return moment(time).format("DD/MM/YY");
}

export function convertDateToString(dateStr, onlyMonth = false) {
  // date format MM-YYYY
  const [month, year] = dateStr.split("-");
  const date = new Date(year, month - 1);

  // Format the date to get the full month name
  const monthYear = date.toLocaleString("default", {
    month: "long",
    year: "numeric",
  });
  if (onlyMonth) {
    const month = date.toLocaleString("default", { month: "long" });
    return month;
  }
  return monthYear;
}

export function getVisualType(data: any) {
  if ("cardIdentifier" in data) return PinnedVisualType.card;
  if ("chartIdentifier" in data) return PinnedVisualType.chart;
  if ("cohortIdentifier" in data) return PinnedVisualType.cohort;
  if ("pivotIdentifier" in data) return PinnedVisualType.pivotTable;
}

export function getChartTime(time, inSameYear = true) {
  if (inSameYear) {
    return moment(time).format("DD/MM");
  } else {
    return moment(time).format("DD/MM/YY");
  }
}

export function dateParsingStandard(time) {
  return moment(time).format("MMM D, YYYY");
}

export function getRelativeTime(time) {
  return moment(time).fromNow();
}

export function ChaanelTime(time) {
  return moment(time).format("Do MMMM, YYYY");
}

export function MonthYear(time) {
  return moment(time).format("LL");
}

export function MonthDateTime(time) {
  return moment(time).utcOffset("+5:30").format("LLL");
}

export function HoursMinute(time) {
  return moment(time).utcOffset("+5:30").format("LT");
}

export function GetHoursMinute(time) {
  return moment(time).utcOffset("+5:30").format("hh:mm A");
}

export function getLocalUser(): User {
  const data = localStorage.getItem("user");
  if (data) {
    let user: User = decryptData(data);
    return user;
  } else {
    return;
  }
}

export function GASendEvent(
  user: User,
  event: GAEvents,
  dimension: Record<string, any>
) {
  try {
    const company_ids = import.meta.env.VITE_MERITO_COMPANY_ID ?? [];

    // check if the user is a test user
    if (company_ids.includes(String(user?.companyId))) return;

    ReactGA.event(event, dimension);
  } catch (error) {
    console.error("error in tracking");
    console.error(error);
  }
}

export function getUnitValue(unit: string, value: number) {
  const formattedValue = formatCommaNumber(value);
  if (unit == "%") return formattedValue + "%";
  else if (unit == "₹") return "₹" + formattedValue;

  return formattedValue;
}

export function getLocalJWT() {
  return localStorage.getItem("jwt_token");
}

// Aborts every in-flight request tied to the current session when logout()
// fires, so a slow request started under the old account can't resolve into
// a newly logged-in account's session. Rotated (not just aborted) so the very
// next login/API call gets a fresh, non-aborted signal.
let sessionAbortController = new AbortController();

export function getSessionAbortSignal() {
  return sessionAbortController.signal;
}

export function getLocalUserId() {
  let userId = localStorage.getItem("userId");
  return decryptData(userId).toString();
}

export function getLocalCompanyId() {
  let companyId = localStorage.getItem("companyId");
  return decryptData(companyId).toString();
}

export function logout() {
  const userActivitySentOnObj = localStorage.getItem("userActivitySentOn");
  localStorage.clear();
  sessionStorage.clear();
  if (userActivitySentOnObj) {
    localStorage.setItem("userActivitySentOn", userActivitySentOnObj);
  }
  sessionAbortController.abort();
  sessionAbortController = new AbortController();
  return true;
}

export function login(token, user: User, isFirstLogin: boolean) {
  localStorage.setItem("jwt_token", token);
  localStorage.setItem("isFirstLogin", isFirstLogin.toString());
  localStorage.setItem("user", encryptData(JSON.stringify(user)));
  localStorage.setItem("userId", encryptData(user.id.toString()));
  localStorage.setItem("companyId", encryptData(user.companyId.toString()));
  if (isFirstLogin) {
    localStorage.setItem(
      "isFirstLogin",
      JSON.stringify({ userId: user.id, firstLogin: true })
    );
    localStorage.setItem(
      "isFirstWorkspaceVisited",
      JSON.stringify({ userId: user.id, isVisited: false })
    );
    localStorage.setItem(
      "isCollaborationHubVisited",
      JSON.stringify({ userId: user.id, isVisited: false })
    );
  }
}

export function getCapital(str: string): String {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

export function isArrIncludes(arr: string[], str: string): boolean {
  return arr.includes(str);
}

export function getNumericString(str: string): string {
  return str.replace(/\D/g, "");
}

export const isNumeric = (val: string): boolean => {
  // console.log(val)
  if (val.toString().trim() == "") {
    return false;
  } else return !isNaN(Number(val));
};

export default function convertToInternationalCurrencySystem(labelValue) {
  // Nine Zeroes for Billions
  return Math.abs(Number(labelValue)) >= 1.0e9
    ? (Math.abs(Number(labelValue)) / 1.0e9).toFixed(1) + "B"
    : // Six Zeroes for Millions
    Math.abs(Number(labelValue)) >= 1.0e6
      ? (Math.abs(Number(labelValue)) / 1.0e6).toFixed(1) + "M"
      : // Three Zeroes for Thousands
      Math.abs(Number(labelValue)) >= 1.0e4
        ? (Math.abs(Number(labelValue)) / 1.0e3).toFixed(1) + "K"
        : Math.round(Number(labelValue) * 10) / 10;
}

export function formatNumber(val) {
  let format = new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
  return convertToInternationalCurrencySystem(val);
}

export function formatCommaNumber(val: number | string, toRound = true) {
  // const str = val.toLocaleString("en-US");
  let shouldRound = false;
  try {
    if (typeof val == "string") {
      shouldRound = val.split(".").length > 1 ? true : false;
    } else {
      shouldRound = val % 1 > 0 ? true : false;
    }
  } catch {
    shouldRound = false;
  }

  if (shouldRound && toRound) {
    const str = isNumeric(val.toString())
      ? Number(val).toLocaleString("en-IN")
      : val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return str;
  } else {
    const str = isNumeric(val.toString())
      ? Number(val).toLocaleString("en-IN")
      : val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return str;
  }
}

export function sumArrays(...arrays) {
  const n = arrays.reduce((max, xs) => Math.max(max, xs.length), 0);
  const result = Array.from({ length: n });
  return result.map((_, i) =>
    arrays
      .map((xs) => xs[i] || 0)
      .reduce((sum, x) => Number(sum) + Number(x), 0)
  );
}

export function getApiUrl(str: string) {
  let isS3 = true ? "S3" : "";
  return str + isS3;
}

export const getReplacedMessage = (message: string) => {
  let replacedMessage = message.replaceAll(
    /@\[([\w ]+)]\(\d+\)/gm,
    "<b>$1</b>"
  );
  return replacedMessage;
};

export const getParsedMessage = (message: string) => {
  const parsedMessage = parse(message);
  return parsedMessage;
};

export const getRandomColor = () => {
  return `#${Math.random().toString(16).substr(-6)}`;
};

export const getRandomChatColor = () => {
  const chatColors = [
    "B8255f",
    "884DFF",
    "AF38EB",
    "E05194",
    "DB4035",
    "FF9933",
    "FAD000",
    "73CC49",
    "299438",
    "158FAD",
    "14AAF5",
    "4073FF",
  ];

  let randomNumber = Math.floor(Math.random() * 12);
  return chatColors[randomNumber];
};

export const creatPdf = async (name, elements, version) => {
  // let img = "";
  // getImgFromUrl(meritoIcon, function (i) {
  //   img = i;
  // });
  // const doc = new jsPDF("p", "px");
  // let top = 20;
  // const padding = 10;
  // for (let i = 0; i < elements.length; i++) {
  //   const el = elements[i] as HTMLElement;
  //   const imgData = await htmlToImage.toPng(el);
  //   let elHeight = el.offsetHeight;
  //   let elWidth = el.offsetWidth;
  //   const pageWidth = doc.internal.pageSize.getWidth();
  //   if (elWidth > pageWidth) {
  //     const ratio = pageWidth / elWidth;
  //     elHeight = elHeight * ratio - padding;
  //     elWidth = elWidth * ratio - padding;
  //   }
  //   const pageHeight = doc.internal.pageSize.getHeight();
  //   if (top + elHeight > pageHeight) {
  //     doc.addPage();
  //     top = 20;
  //   }
  //   doc.setFontSize(12);
  //   doc.setTextColor("#313131");
  //   doc.text(version, pageWidth - 100, top);
  //   doc.addImage(img, padding, top - 8, 12, 12);
  //   doc.setTextColor("#F73E77");
  //   doc.text(name, padding + 20, top);
  //   doc.addImage(
  //     imgData,
  //     "PNG",
  //     padding,
  //     top + 5,
  //     elWidth,
  //     elHeight,
  //     `image${i}`
  //   );
  //   top += 30;
  //   top += elHeight;
  // }
  // doc.save(`${name}.pdf`);
};

function getImgFromUrl(logo_url, callback) {
  var img = new Image();
  img.src = logo_url;
  img.height = 16;
  img.width = 16;
  img.onload = function () {
    callback(img);
  };
}

export const encryptData = (data) => {
  let salt = import.meta.env.VITE_SALT_SECRATE;
  return CryptoJS.AES.encrypt(data, salt).toString();
};

export const decryptData = (ciphertext) => {
  if (!ciphertext) return "";

  try {
    let salt = import.meta.env.VITE_SALT_SECRATE;
    const bytes = CryptoJS.AES.decrypt(ciphertext, salt);
    return JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
  } catch (err) {
    logout();
    window.location.reload();
    return "";
  }
};

export const pdfColors = {
  pinkColor: "#F73E77",
  textColor: "#212121",
  borderColor: "#100828",
  textGray: "#808080",
  brandColor: "#100828",
  black50: "#00000080",
  bgColor: "#f0eff4",
  textBlack55: "#555555",
  textBlack69: "#696b6f",
  whiteF9: "#f9f9f9",
};

export const customDataRangeEnum = {
  1: "Month", //month
  2: "Week", //week
  3: "Day", //day
  4: "Current Quarter",
  5: "Current Month",
  6: "Current Week",
};

export const getDataPeriod = (type, value) => {
  if ([1, 2, 3].includes(type)) {
    return (
      "Last " +
      value +
      " " +
      customDataRangeEnum[type] +
      `${value > 1 ? "s" : ""}`
    );
  } else return customDataRangeEnum[type];
};

const daysMultiplierByType = {
  1: 30,
  2: 7,
  3: 1,
};

export const getConvertDatePeriod = (
  type: number,
  value: number,
  todayDate: any = null,
  referenceDataPeriod: string[] = [],
  isComparison: boolean = false,
  matchDayOfWeek: boolean = false
): { dataPeriod: string[]; days: string; type: number; value: number } => {
  // console.log('type', type);
  let referenceStartDate: moment.Moment | null = null;
  let referenceEndDate: moment.Moment | null = null;
  let differenceInDays = 0;
  if (isComparison && referenceDataPeriod.length) {
    referenceStartDate = moment(referenceDataPeriod[0], "DD-MM-YYYY");
    referenceEndDate = moment(referenceDataPeriod[1], "DD-MM-YYYY");
    differenceInDays = referenceEndDate.diff(referenceStartDate, "days");
  }

  if (type == 4) {
    const startDate = todayDate
      ? moment(todayDate).startOf("quarter").format("DD-MM-YYYY")
      : moment().startOf("quarter").format("DD-MM-YYYY");
    const endDate = todayDate
      ? moment(todayDate).format("DD-MM-YYYY")
      : moment().format("DD-MM-YYYY");
    return {
      dataPeriod: [startDate, endDate],
      type,
      value: 0,
      days: "0",
    };
  } else if (type == 5) {
    // this month and previous month
    let startDate = null;
    let endDate = null;

    if (isComparison) {
      if (matchDayOfWeek) {
        // startDate = moment(referenceStartDate).clone().subtract(1, "month").day(referenceStartDate.day()).format("DD-MM-YYYY");
        startDate = moment(referenceStartDate)
          .clone()
          .subtract(28, "days")
          .format("DD-MM-YYYY");
      } else {
        startDate = moment(referenceStartDate)
          .clone()
          .subtract(1, "month")
          .format("DD-MM-YYYY");
      }
      // console.log('startDate', startDate)
      endDate = moment(startDate, "DD-MM-YYYY")
        .clone()
        .add(differenceInDays, "days")
        .format("DD-MM-YYYY");
    } else {
      startDate = todayDate
        ? moment(todayDate).startOf("month").format("DD-MM-YYYY")
        : moment().startOf("month").format("DD-MM-YYYY");
      endDate = todayDate
        ? moment(todayDate).format("DD-MM-YYYY")
        : moment().format("DD-MM-YYYY");
    }
    return {
      dataPeriod: [startDate, endDate],
      type,
      value: 0,
      days: "0",
    };
  } else if (type == 6) {
    // this week and previous week
    let startDate = null;
    let endDate = null;

    if (isComparison) {
      startDate = moment(referenceStartDate)
        .clone()
        .subtract(1, "week")
        .format("DD-MM-YYYY");
      console.log("startDate", startDate);
      endDate = moment(startDate, "DD-MM-YYYY")
        .clone()
        .add(differenceInDays, "days")
        .format("DD-MM-YYYY");
    } else {
      startDate = todayDate
        ? moment(todayDate).startOf("week").format("DD-MM-YYYY")
        : moment().startOf("week").format("DD-MM-YYYY");
      endDate = todayDate
        ? moment(todayDate).format("DD-MM-YYYY")
        : moment().format("DD-MM-YYYY");
    }
    return {
      dataPeriod: [startDate, endDate],
      type,
      value: 0,
      days: "0",
    };
  } else if (type == 7) {
    const startDate = todayDate
      ? moment(todayDate).add(1, "day").subtract(7, "days").format("DD-MM-YYYY")
      : moment().add(1, "day").subtract(7, "days").format("DD-MM-YYYY");
    const endDate = todayDate
      ? moment(todayDate).format("DD-MM-YYYY")
      : moment().format("DD-MM-YYYY");
    return {
      dataPeriod: [startDate, endDate],
      type,
      value: 0,
      days: "0",
    };
  } else if (type == 8) {
    const startDate = todayDate
      ? moment(todayDate)
        .add(1, "day")
        .subtract(30, "days")
        .format("DD-MM-YYYY")
      : moment().add(1, "day").subtract(30, "days").format("DD-MM-YYYY");
    const endDate = todayDate
      ? moment(todayDate).format("DD-MM-YYYY")
      : moment().format("DD-MM-YYYY");
    return {
      dataPeriod: [startDate, endDate],
      type,
      value: 0,
      days: "0",
    };
  } else if (type == 14) {
    const startDate = todayDate
      ? moment(todayDate)
        .add(1, "day")
        .subtract(60, "days")
        .format("DD-MM-YYYY")
      : moment().add(1, "day").subtract(60, "days").format("DD-MM-YYYY");
    const endDate = todayDate
      ? moment(todayDate).format("DD-MM-YYYY")
      : moment().format("DD-MM-YYYY");
    return {
      dataPeriod: [startDate, endDate],
      type,
      value: 0,
      days: "0",
    };
  } else if (type == 9) {
    const startDate = todayDate
      ? moment(todayDate)
        .add(1, "day")
        .subtract(90, "days")
        .format("DD-MM-YYYY")
      : moment().add(1, "day").subtract(90, "days").format("DD-MM-YYYY");
    const endDate = todayDate
      ? moment(todayDate).format("DD-MM-YYYY")
      : moment().format("DD-MM-YYYY");
    return {
      dataPeriod: [startDate, endDate],
      type,
      value: 0,
      days: "0",
    };
  } else if (type == 12) {
    // last 12 months and previous year

    let startDate = null;
    let endDate = null;

    if (isComparison) {
      if (matchDayOfWeek) {
        // startDate = moment(referenceStartDate).clone().subtract(1, "year").day(referenceStartDate.day()).format("DD-MM-YYYY");
        startDate = moment(referenceStartDate)
          .clone()
          .subtract(52, "weeks")
          .format("DD-MM-YYYY");
      } else {
        startDate = moment(referenceStartDate)
          .clone()
          .subtract(1, "year")
          .format("DD-MM-YYYY");
      }
      // console.log('startDate', startDate)
      endDate = moment(startDate, "DD-MM-YYYY")
        .clone()
        .add(differenceInDays, "days")
        .format("DD-MM-YYYY");
    } else {
      startDate = todayDate
        ? moment(todayDate)
          .subtract(12, "month")
          .startOf("month")
          .format("DD-MM-YYYY")
        : moment().startOf("month").subtract(12, "month").format("DD-MM-YYYY");
      endDate = todayDate
        ? moment(todayDate).format("DD-MM-YYYY")
        : moment().format("DD-MM-YYYY");
    }
    // const startDate = todayDate
    //     ? moment(todayDate)
    //         .subtract(12, "month")
    //         .startOf("month")
    //         .format("DD-MM-YYYY")
    //     : moment().startOf("month").subtract(12, "month").format("DD-MM-YYYY");
    // const endDate = todayDate
    //     ? moment(todayDate).format("DD-MM-YYYY")
    //     : moment().format("DD-MM-YYYY");
    return {
      dataPeriod: [startDate, endDate],
      type,
      value: 0,
      days: "0",
    };
  } else if (type == 13) {
    const startDate = todayDate
      ? moment(todayDate)
        .subtract(6, "month")
        .startOf("month")
        .format("DD-MM-YYYY")
      : moment().startOf("month").subtract(6, "month").format("DD-MM-YYYY");
    const endDate = todayDate
      ? moment(todayDate).format("DD-MM-YYYY")
      : moment().format("DD-MM-YYYY");
    return {
      dataPeriod: [startDate, endDate],
      type,
      value: 0,
      days: "0",
    };
  } else if (type == 18) {
    const startDate = todayDate
      ? moment(todayDate)
        .subtract(18, "month")
        .startOf("month")
        .format("DD-MM-YYYY")
      : moment().startOf("month").subtract(18, "month").format("DD-MM-YYYY");
    const endDate = todayDate
      ? moment(todayDate).format("DD-MM-YYYY")
      : moment().format("DD-MM-YYYY");
    return {
      dataPeriod: [startDate, endDate],
      type,
      value: 0,
      days: "0",
    };
  } else {
    let dateSubtractor = daysMultiplierByType[type] * value;
    // console.log(dateSubtractor);
    const currentDate = todayDate ? moment(todayDate) : moment();
    const startDate = currentDate.subtract(
      type == 2 ? dateSubtractor - 1 : dateSubtractor,
      "days"
    );
    const formattedStartDate = startDate.format("DD-MM-YYYY");
    const formattedEndDate = todayDate
      ? moment(todayDate).format("DD-MM-YYYY")
      : moment().format("DD-MM-YYYY");
    return {
      dataPeriod: [formattedStartDate, formattedEndDate],
      days: dateSubtractor.toString(),
      type,
      value,
    };
  }
};

export const getDateRangeText = (
  type: number,
  value: number,
  dataPeriod: string[]
) => {
  switch (type) {
    case 1:
      if (value > 1) {
        return `Last ${value} Months | ${dataPeriod[0]} - ${dataPeriod[1]}`;
      } else {
        return `Last ${value} Month | ${dataPeriod[0]} - ${dataPeriod[1]}`;
      }
      break;
    case 2:
      if (value > 1) {
        return `Last ${value} Weeks | ${dataPeriod[0]} - ${dataPeriod[1]}`;
      } else {
        return `Last ${value} Week | ${dataPeriod[0]} - ${dataPeriod[1]}`;
      }
      break;
    case 3:
      if (value > 1) {
        return `Last ${value} Days | ${dataPeriod[0]} - ${dataPeriod[1]}`;
      } else {
        return `Last ${value} Day | ${dataPeriod[0]} - ${dataPeriod[1]}`;
      }
      break;
    case 4:
      return `Current Quarter | ${dataPeriod[0]} - ${dataPeriod[1]}`;
      break;
    case 5:
      return `Current Month | ${dataPeriod[0]} - ${dataPeriod[1]}`;
      break;
    case 6:
      return `Current Week | ${dataPeriod[0]} - ${dataPeriod[1]}`;
      break;
    default:
      return "";
      break;
  }
  // if(type == 4) {
  //   return `Current Quarter | ${dataPeriod[0]} - ${dataPeriod[1]}`
  // } else if(type == 5) {
  //   return `Current Month | ${dataPeriod[0]} - ${dataPeriod[1]}`
  // } else if(type == 6) {
  //   return `Current Week | ${dataPeriod[0]} - ${dataPeriod[1]}`
  // }
};

export const joinArray = (arr, joinWith = "and") => {
  let outStr = "";
  if (arr.length === 1) {
    outStr = arr[0];
  } else if (arr.length === 2) {
    outStr = arr.join(` ${joinWith} `);
  } else if (arr.length > 2) {
    outStr = arr.slice(0, -1).join(", ") + ` ${joinWith} ` + arr.slice(-1);
  }
  return outStr;
};

export const IpColorEnum = {
  Shopify: "#95BF47",
  "Google Ads": "#FCBC03",
  "Meta Ads": "#4385F5",
  "Google Analytics": "#E37400",
  "Shopify,Google Ads": "#70A596",
  "Shopify,Meta Ads": "#BC38FA",
  "Shopify,Google Analytics": "#910F63",
  "Google Ads,Meta Ads": "#F5AEAE",
  "Shopify,Google Ads,Meta Ads": "#385014",
  "Amazon Seller Central": "#FF9900",
  "Amazon Vendor Central": "#87DAE3",
  "Amazon Ads": "#232F3E",
  "Amazon Seller Central,Amazon Ads": "#FB98B7",
  "Amazon Vendor Central,Amazon Ads": "#9B30FF",
  "Amazon Seller Central,Amazon Vendor Central": "#228B22",
  "Amazon Seller Central,Amazon Vendor Central,Amazon Ads": "#D30035",
  // "Flipkart Seller": "#FFDE14"
};

export const convertUTCtoLocal = (
  dateString: string,
  format: "ISO" | string = "ISO"
): Moment => {
  /**
   * @param dateString: string
   * @param format: "ISO" | string
   * @returns date object in local timezone
   * @description converts UTC date string to local date object
   */

  const localOffset = moment().utcOffset();
  if (format == "ISO") {
    return moment(dateString).utcOffset(localOffset);
  }

  return moment(dateString, format).utcOffset(localOffset);
};

export const formatNumberWithComma = (
  value,
  roundOffTo = 0,
  convertToLakhs = false
) => {
  const LAKHS = 100000;

  if (!isNaN(value)) {
    // First, convert the value to a number and round it off
    const roundedValue = value
      ? convertToLakhs
        ? Number(Number(value) / LAKHS).toFixed(roundOffTo)
        : Number(Number(value).toFixed(roundOffTo))
      : value;

    // Then, format it with commas
    return roundedValue.toLocaleString("en-IN");
  } else {
    return value;
  }
};

export const formatNumberWithCommasAndUnits = (value, unit: pnlFormat) => {
  const LAKHS = 100000;
  const THOUSANDS = 1000;
  let number;
  let roundOffTo;

  // Check if it's a valid number
  if (!isNaN(value)) {
    // Convert to a number
    number = Number(value);

    // Decide the unit of conversion and rounding off based on value and unit provided
    if (unit === pnlFormat.lakhs) {
      if (Math.abs(number) >= 10000) {
        number /= LAKHS;
        roundOffTo = 1;
      } else if (Math.abs(number) >= 1000) {
        number /= LAKHS;
        roundOffTo = 2;
      } else {
        number /= LAKHS;
        roundOffTo = 3;
      }
    } else if (unit === pnlFormat.thousands) {
      number /= THOUSANDS;
      roundOffTo = Math.abs(number) >= 100 ? 1 : 2;
    } else {
      // If no unit is provided, just round off to integers
      roundOffTo = 0;
    }

    if (number == 0) {
      return 0;
    }

    // Fix the decimal places
    number = number.toFixed(roundOffTo);

    // console.log('coming into this', number)

    // Format with commas
    return Number(number).toLocaleString("en-IN");
  }

  // Return the original value if it's not a number
  return value;
};

export const getLast12CYMonthsArr = (): string[] => {
  const months: string[] = [];
  const date = new Date();
  date.setDate(1);

  for (let i = 1; i <= 12; i++) {
    date.setMonth(date.getMonth() - 1);
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    months.push(`${month}-${year}`);
  }

  return months;
};

export const getLast12CYMonthsStr = (): string => {
  const months: string[] = [];
  const date = new Date();
  date.setDate(1);

  for (let i = 1; i <= 12; i++) {
    date.setMonth(date.getMonth() - 1);
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    months.push(`'${month}-${year}'`);
  }

  return months.join(", ");
};

// Custom mutator to set field as touched
export const setFieldTouchedMutator = ([name], state, { changeValue }) => {
  // const fieldName = args[0];
  const field = state.fields[name];
  // Check if the field exists and hasn't been touched yet
  if (field && !field.touched) {
    // Mark the field as touched
    field.touched = true;
    // Optionally, force re-validation if necessary
    changeValue(state, name, (value) => value);
  }
};

export const parseNumberForDB = (
  formattedValue = "",
  canContainDecimals = false
) => {
  // Remove thousands separators (commas) and handle decimal point
  // console.log('formattedValue', formattedValue)
  let numericValue;
  if (canContainDecimals) {
    // console.log('formattedValue', formattedValue)
    // For decimal values, parse as float after removing commas
    if (formattedValue.toString().match(/\.$/)) {
      return formattedValue.toString();
    }
    numericValue = parseFloat(formattedValue.toString().replace(/,/g, ""));
  } else {
    // console.log('formattedValue', canContainDecimals)
    // For integer values, parse as int (removing decimals if any)
    numericValue = formattedValue
      ? parseInt(formattedValue.toString().replace(/,/g, "").split(".")[0], 10)
      : "";
  }
  return isNaN(numericValue) ? "" : numericValue;
};

export const onBlurFormatNumber = (value, roundOffDecimals = 0) => {
  if (value === "") return "";
  // const numericValue = Number(value.toString().replace(/,/g, '')).toFixed(roundOffDecimals);
  // console.log('numericValue', numericValue)
  const containsDecimal = value.toString().includes(".");
  let numericValue;

  if (containsDecimal) {
    const [integerPart, decimalPart] = value.toString().split(".");
    // // Value has decimals and requires rounding
    // numericValue = Number(value.toString().replace(/,/g, '')).toFixed(roundOffDecimals);
    // // console.log('numeric values', numericValue)
    if (decimalPart.length > roundOffDecimals && roundOffDecimals > 0) {
      // Value has more decimals than required, round it off
      numericValue = Number(value.toString().replace(/,/g, "")).toFixed(
        roundOffDecimals
      );
    } else {
      // Value has decimals within the limit or roundOffDecimals is 0, keep it as is
      numericValue = Number(value.toString().replace(/,/g, "")).toString();
    }
  } else {
    // Value does not require decimals or rounding off
    numericValue = Number(value.toString().replace(/,/g, ""));
    // Convert it back to a string to maintain consistency in return type
    numericValue = numericValue.toString();
  }

  return isNaN(Number(numericValue)) ? "" : numericValue;
};

export const findFirstErrorField = (errors, parentKey = "") => {
  for (const key in errors) {
    const error = errors[key];
    const fieldName = parentKey ? `${parentKey}.${key}` : key;
    if (error) {
      if (typeof error === "object" && !Array.isArray(error)) {
        const nestedErrorField = findFirstErrorField(error, fieldName);
        if (nestedErrorField) {
          return nestedErrorField;
        }
      } else if (Array.isArray(error)) {
        for (let i = 0; i < error.length; i++) {
          if (error[i]) {
            const arrayFieldName = `${fieldName}[${i}]`;
            if (typeof error[i] === "object") {
              const nestedErrorField = findFirstErrorField(
                error[i],
                arrayFieldName
              );
              if (nestedErrorField) {
                return nestedErrorField;
              }
            } else {
              return arrayFieldName;
            }
          }
        }
      } else {
        return fieldName;
      }
    }
  }
  return null;
};

// Encrypt function using AES-256-CBC
export const encryptDataByCrypto = (data) => {
  const salt = import.meta.env.VITE_SALT_SECRATE; // Static salt
  const key = CryptoJS.SHA256(salt).toString(CryptoJS.enc.Hex); // Convert salt to a 32-byte key in Hex

  // Generate a 16-byte IV using browser crypto API
  const ivArray = window.crypto.getRandomValues(new Uint8Array(16)); // 16-byte IV
  const ivHex = Array.from(ivArray)
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join(""); // Convert to Hex

  const encrypted = CryptoJS.AES.encrypt(data, CryptoJS.enc.Hex.parse(key), {
    iv: CryptoJS.enc.Hex.parse(ivHex), // Use IV in Hex
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  });

  return `${ivHex}.${encrypted.toString()}`; // Store IV in Hex format
};

export const setAgencyClient = (companyId: number) => {
  localStorage.setItem("currentAgencyClientId", companyId.toString());
};

export const removeAgencyClient = () => {
  localStorage.removeItem("currentAgencyClientId");
};

export const removeSpace = (str: string) => {
  return str.replace(/\s+/g, "");
};

export const digitsOnly = (str: string) => {
  return str.replace(/\D/g, "");
};

export function toLakhs(value: string | number, decimals = 1): string {
  const num =
    typeof value === "number"
      ? value
      : Number(String(value).replace(/[^0-9.-]/g, ""));
  if (!Number.isFinite(num)) {
    throw new Error("toLakhs: invalid number input");
  }
  const lakhs = num / 100_000;
  const factor = Math.pow(10, decimals);
  const rounded = Math.round(lakhs * factor) / factor;
  const str = rounded
    .toFixed(decimals)
    .replace(/\.0+$/, "")
    .replace(/(\.\d*[1-9])0+$/, "$1");
  return str;
}

export type Direction = "INCREASE" | "DECREASE";
export type Growth = {
  positiveState?: Direction | string | null;
  value?: number | null; // unused for this check
  type?: Direction | string | null;
};

/**
 * Returns true iff positiveState and type are the same direction.
 * Example:
 *  { positiveState: "INCREASE", type: "INCREASE" } -> true
 *  { positiveState: "INCREASE", type: "DECREASE" } -> false
 */
export function isPositiveGrowth(g: Growth): boolean {
  if (!g) return false;

  const norm = (s?: string | null) =>
    String(s ?? "")
      .trim()
      .toUpperCase();
  const pos = norm(g.positiveState);
  const typ = norm(g.type);

  if (
    !["INCREASE", "DECREASE"].includes(pos) ||
    !["INCREASE", "DECREASE"].includes(typ)
  ) {
    return false; // invalid values -> false
  }

  return pos === typ;
}

export function isNegativeFillColor(
  metricValue: number,
  metricType: string,
  target: number | null
): boolean {
  const numOfDaysInMonth = moment().daysInMonth();
  const todayDate = moment().date();
  const expectedTillNow = (target / numOfDaysInMonth) * (todayDate - 1);
  console.log("expectedTillNow for color", expectedTillNow);
  console.log("metricValue for color", metricValue);
  if (metricType === "sales") {
    if (metricValue < expectedTillNow - expectedTillNow * 0.05) {
      return true;
    } else {
      return false;
    }
  } else {
    if (metricValue < expectedTillNow + expectedTillNow * 0.05) {
      return false;
    } else {
      return true;
    }
  }
}

/** Remove a trailing phrase (case-insensitive, whitespace-flexible). */
export function stripTrailingPhrase(input: string, trailing: string): string {
  const src = input.trim().split(/\s+/);
  const suf = trailing.trim().split(/\s+/);
  if (suf.length === 0 || src.length === 0) return input.trim();

  for (let i = 0; i < suf.length; i++) {
    const a = src[src.length - 1 - i]?.toLowerCase();
    const b = suf[suf.length - 1 - i]?.toLowerCase();
    if (a !== b) return input.trim();
  }
  return src.slice(0, src.length - suf.length).join(" ");
}

export function getPartnerLogo(state: RootState, ip: string): string | null {
  // console.log("ip", ip);
  const partner = Object.values(state.config.integrationPartners).find(
    (p: any) => p._id === ip
  );
  // console.log("partner", partner);
  return partner?.logo ?? null;
}

export function calculateProgress(
  value: number,
  target: number
): { dailyPerc: number; dailyRemaining: number; idealPerc: number } {
  if (!target) return { dailyPerc: 0, dailyRemaining: 0, idealPerc: 0 };

  const daysInMonth = moment().daysInMonth();
  const todayDate = moment().subtract(1, 'day').date();
  console.log("value", value);
  console.log("target", target);
  console.log("daysInMonth", daysInMonth);

  // Linear daily progression - ideal target for today
  const expectedTillNow = (target / daysInMonth) * todayDate;

  // Actual percentage of monthly target achieved
  const percentage = (value / target) * 100;

  // Ideal percentage for today (linear progression)
  const idealPerc = (todayDate / daysInMonth) * 100;

  console.log("expectedTillNow (ideal)", expectedTillNow);
  console.log("percentage of target", percentage);
  console.log("ideal percentage for today", idealPerc);

  return {
    dailyPerc: Math.round(percentage * 10) / 10,
    dailyRemaining: expectedTillNow - value,
    idealPerc: Math.round(idealPerc * 10) / 10,
  };
}

// primarily for getTargetCasesMessage Function
export function formatDailyRemaining(num: number): string {
  if (num < 100000) {
    return `${(num / 1000).toFixed(1)}k`;
  }
  const inLakhs = Math.floor(num / 100000);
  return `${inLakhs.toLocaleString("en-IN")} lakhs`;
}

type YM = { year: number; month: number };
type DataPeriod = { startMonth: YM; endMonth?: YM };

function getRanges(dataPeriod: DataPeriod) {
  // const MS_DAY = 24 * 60 * 60 * 1000;
  const today = new Date();

  // const startOfDay = (d: Date) =>
  //   new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const lastDayOfMonth = (y: number, m: number) => new Date(y, m + 1, 0);

  const { startMonth, endMonth } = dataPeriod;

  const startDate = new Date(startMonth.year, startMonth.month, 1);

  let endDate: Date;
  const isEndMonthCurrent =
    endMonth &&
    endMonth.year === today.getFullYear() &&
    endMonth.month === today.getMonth();

  if (endMonth) {
    // If end month is current month, use yesterday; otherwise use last day of month
    if (isEndMonthCurrent) {
      endDate = new Date(today);
      endDate.setDate(today.getDate() - 1);
    } else {
      endDate = lastDayOfMonth(endMonth.year, endMonth.month);
    }
  } else {
    const isStartCurrent =
      startMonth.year === today.getFullYear() &&
      startMonth.month === today.getMonth();

    // If start month is current month and no end month, use yesterday
    if (isStartCurrent) {
      endDate = new Date(today);
      endDate.setDate(today.getDate() - 1);
    } else {
      endDate = lastDayOfMonth(startMonth.year, startMonth.month);
    }
  }

  let prevStart: Date;
  let prevEnd: Date;

  if (!endMonth) {
    // const isStartCurrent =
    //   startMonth.year === today.getFullYear() &&
    //   startMonth.month === today.getMonth();

    prevStart = new Date(startMonth.year, startMonth.month - 1, 1);
    prevEnd = lastDayOfMonth(startMonth.year, startMonth.month - 1);
  } else {
    const monthCount =
      (endMonth.year - startMonth.year) * 12 +
      (endMonth.month - startMonth.month + 1);

    prevStart = new Date(
      startDate.getFullYear(),
      startDate.getMonth() - monthCount,
      1
    );
    const shiftedEndY = endDate.getFullYear();
    const shiftedEndM = endDate.getMonth() - monthCount;
    prevEnd = lastDayOfMonth(shiftedEndY, shiftedEndM);
  }

  return { startDate, endDate, prevStart, prevEnd };
}

function buildTempDataPeriod(dataPeriod) {
  if (
    !dataPeriod ||
    typeof dataPeriod.startMonth?.month !== "number" ||
    dataPeriod.startMonth?.month < 0 ||
    dataPeriod.startMonth?.month > 11
  ) {
    return null;
  }

  const today = new Date();

  const startDate = new Date(
    dataPeriod?.startMonth?.year,
    dataPeriod?.startMonth?.month,
    1
  );

  let endDate: Date;
  if (
    typeof dataPeriod.endMonth?.month === "number" &&
    dataPeriod.endMonth?.month >= 0 &&
    dataPeriod.endMonth?.month <= 11
  ) {
    endDate = new Date(
      dataPeriod?.endMonth?.year,
      dataPeriod.endMonth?.month + 1,
      0
    );
  } else {
    endDate = new Date(dataPeriod.endMonth?.year, dataPeriod.startMonth + 1, 0);
  }

  //format DDMM YYYY
  const formatDate = (d: Date) =>
    `${String(d.getDate()).padStart(2, "0")}-${String(
      d.getMonth() + 1
    ).padStart(2, "0")}-${d.getFullYear()}`;

  // let prevStart: Date;
  // let prevEnd: Date;

  // if (dataPeriod.endMonth || typeof dataPeriod.endMonth?.month !== "number") {
  //   // Case 1: endMonth is NOT current month

  //   if (
  //     dataPeriod.endMonth.month !== today.getMonth() ||
  //     dataPeriod.endMonth.year !== today.getFullYear()
  //   ) {
  //     console.log("endMonth is NOT current month");
  //     if (dataPeriod?.startMonth?.year === dataPeriod?.endMonth?.year) {
  //       console.log("SAME YEAR");
  //       let rangeMeasure =
  //         dataPeriod?.endMonth?.month - dataPeriod?.startMonth?.month + 1;
  //       prevStart = new Date(
  //         dataPeriod?.startMonth?.month - rangeMeasure < 0
  //           ? dataPeriod?.startMonth?.year - 1
  //           : dataPeriod?.startMonth?.year,
  //         dataPeriod?.startMonth?.month - rangeMeasure < 0
  //           ? 12 + rangeMeasure
  //           : dataPeriod?.startMonth?.month - rangeMeasure
  //       );

  //       prevEnd = new Date(
  //         dataPeriod?.endMonth?.month - rangeMeasure < 0
  //           ? dataPeriod?.endMonth?.year - 1
  //           : dataPeriod?.endMonth?.year,
  //         dataPeriod?.endMonth?.month - rangeMeasure < 0
  //           ? 12 + rangeMeasure
  //           : dataPeriod?.endMonth?.month - rangeMeasure
  //       );
  //     } else {
  //       console.log("DIFF YEAR");
  //       let rangeMeasure =
  //         12 * (dataPeriod?.endMonth?.year - dataPeriod?.startMonth?.year - 1) +
  //         dataPeriod?.endMonth?.month +
  //         1 +
  //         12 -
  //         dataPeriod?.startMonth?.month;

  //       const yearsCount = rangeMeasure / 12;
  //       rangeMeasure = rangeMeasure % 12;

  //       prevStart = new Date(
  //         dataPeriod?.startMonth?.month - rangeMeasure < 0
  //           ? dataPeriod?.startMonth?.year - 1 - yearsCount
  //           : dataPeriod?.startMonth?.year - yearsCount,
  //         dataPeriod?.startMonth?.month - rangeMeasure < 0
  //           ? 12 + rangeMeasure
  //           : dataPeriod?.startMonth?.month - rangeMeasure
  //       );
  //       prevEnd = new Date(
  //         dataPeriod?.endMonth?.month - rangeMeasure < 0
  //           ? dataPeriod?.endMonth?.year - 1 - yearsCount
  //           : dataPeriod?.endMonth?.year - yearsCount,
  //         dataPeriod?.endMonth?.month - rangeMeasure < 0
  //           ? 12 + rangeMeasure
  //           : dataPeriod?.endMonth?.month - rangeMeasure
  //       );
  //     }
  //   } else {
  //     // Case 2: endMonth is current month
  //     console.log("END M IS CURR MONTH");
  //     const { startMonth, endMonth } = dataPeriod;

  //     if (
  //       endMonth?.month === today.getMonth() &&
  //       endMonth?.year === today.getFullYear()
  //     ) {
  //       const startDate = new Date(startMonth.year, startMonth.month, 1); // first day of startMonth

  //       let endDate;

  //       if (
  //         endMonth.year === today.getFullYear() &&
  //         endMonth.month === today.getMonth()
  //       ) {
  //         endDate = today; // current day if endMonth is this month
  //       } else {
  //         // pick same day-of-month as today or last day of endMonth if today > month length
  //         const lastDayOfEndMonth = new Date(
  //           endMonth.year,
  //           endMonth.month + 1,
  //           0
  //         ).getDate();
  //         const day = Math.min(today.getDate(), lastDayOfEndMonth);
  //         endDate = new Date(endMonth.year, endMonth.month, day);
  //       }

  //       //  Ensure both are real Date objects
  //       if (!(startDate instanceof Date)) {
  //         throw new Error("startDate is invalid");
  //       }
  //       if (!(endDate instanceof Date)) {
  //         throw new Error("endDate is invalid");
  //       }

  //       // 3 Number of days in current range
  //       const dayCount =
  //         Math.floor(
  //           (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)
  //         ) + 1;

  //       //  Previous range
  //       const prevEnd = new Date(startDate.getTime() - 1 * 24 * 60 * 60 * 1000);
  //       const prevStart = new Date(
  //         prevEnd.getTime() - (dayCount - 1) * 24 * 60 * 60 * 1000
  //       );
  //     }
  //   }
  // } else {
  //   // Case 3: only startMonth
  //   console.log("only S M");
  //   if (
  //     dataPeriod.startMonth.month === today.getMonth() &&
  //     dataPeriod.startMonth.year === today.getFullYear()
  //   ) {
  //     const rangeDays = Math.ceil(
  //       (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24) + 1
  //     );
  //     prevEnd = new Date(
  //       dataPeriod.startMonth.year,
  //       dataPeriod.startMonth.month,
  //       0
  //     );
  //     prevStart = new Date(prevEnd);
  //     prevStart.setDate(prevEnd.getDate() - rangeDays + 1);
  //   } else {
  //     prevStart = new Date(
  //       dataPeriod.startMonth.year,
  //       dataPeriod.startMonth.month - 1,
  //       1
  //     );
  //     prevEnd = new Date(
  //       dataPeriod.startMonth.year,
  //       dataPeriod.startMonth.month,
  //       0
  //     );
  //   }
  // }

  const { prevStart, prevEnd } = getRanges(dataPeriod);
  endDate = getRanges(dataPeriod).endDate;

  const prevMonthDataPeriod = [formatDate(prevStart), formatDate(prevEnd)];

  return {
    isTemp: true,
    dataPeriod: {
      thisMonthDataPeriod: [formatDate(startDate), formatDate(endDate)],
      prevMonthDataPeriod,
    },
  };
}

export function getAgencyDashboardMetrics(
  dispatch: any,
  dataPeriod: {
    startMonth: {
      year: number;
      month: number;
    };
    endMonth: {
      month: number;
      year: number;
    };
  } | null
) {
  // console.log("dataPeriod", dataPeriod);
  // console.log("values here", buildTempDataPeriod(dataPeriod));

  const values = buildTempDataPeriod(dataPeriod);
  if (
    values &&
    !(
      dataPeriod?.startMonth?.month === new Date().getMonth() &&
      dataPeriod?.startMonth?.year === new Date().getFullYear()
    )
  ) {
    console.log("agencyDashboard api body", values);
    RestS.getDashboardMetrics(values).then((res) => {
      if (res.status == "IN_PROGRESS" || res.status === "INSUFFICIENT_DATA") {
        dispatch(setCustomDataPeiodJobId(res.jobId));
        dispatch(setCustomDataPeiodStatus(res.status));
        console.log("custom data res is in progress");
      } else if (res.status == AgJobsStatus.noClientsConnected) {
        dispatch(setCustomDataPeiodJobId(null));
        dispatch(setCustomDataPeiodStatus(res.status));
        console.log("custom data res is noClientsConnected");
      } else if (res.status == "SUCCESS") {
        dispatch(setData(res));
        dispatch(setCustomDataPeiodStatus(res.status));
        console.log("custom data res is success");
      }
    });
  } else {
    RestS.getDashboardMetricsWithoutBody().then((res) => {
      if (res.status == "IN_PROGRESS" || res.status === "INSUFFICIENT_DATA") {
        dispatch(setJobId(res.jobId));
        dispatch(setStatus(res.status));
        console.log("res is in progress");
      } else if (res.status == AgJobsStatus.noClientsConnected) {
        dispatch(setData(null));
        dispatch(setStatus(res.status));
        console.log("custom data res is noClientsConnected");
      } else if (res.status == "SUCCESS") {
        dispatch(setData(res));
        dispatch(setStatus(res.status));
        console.log("res is success");
      }
    });
  }
}

export function getMonthShortName(dateStr) {
  // console.log("DATE STR", dateStr);
  if (!dateStr) return null;
  const [day, month, year] = dateStr?.split("-");
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  return date.toLocaleString("en-US", { month: "short" });
}
export function getYearDataPeriod(dateStr1, dateStr2) {
  if (!dateStr1 || !dateStr2) {
    return {
      startYear: null,
      endYear: null,
    };
  }
  const [day1, month1, year1] = dateStr1?.split("-");
  const [day2, month2, year2] = dateStr2?.split("-");

  return {
    startYear: year1,
    endYear: year2,
  };
}

export const getTemporaryUnavailableIps = () => {
  const tempUnavailableIps = import.meta.env.VITE_IP_TEMPORARY_DISABLE;
  return tempUnavailableIps ? new Set(tempUnavailableIps.split(",")) : new Set();
}

